import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, FileSpreadsheet } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import ServiceScheduleEditor from "@/components/ServiceScheduleEditor";
import BulkServiceUpload from "@/components/BulkServiceUpload";
import { ALL_DAYS, scheduleSummary } from "@/lib/slots";

// When a service can be booked. Empty slots: the business's general slots.
const emptySchedule = { slots: [], days: ALL_DAYS, available_from: null, available_until: null, capacity: null };
const empty = { name: "", description: "", price: 0, discount_price: null, duration_minutes: 60, home_service: true, center_visit: false, requires_enquiry: false, images: [], category_id: "", seller_ids: [], schedule: emptySchedule };

export default function TAdminServices() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [generalSlots, setGeneralSlots] = useState([]);
  const [saving, setSaving] = useState(false);
  const [bulk, setBulk] = useState(false);          // the bulk upload panel is open

  const load = async () => {
    const [s, c, sl, me] = await Promise.all([api.get("/tenant/services"), api.get("/tenant/categories"), api.get("/tenant/sellers"), api.get("/tenant/me")]);
    setServices(s.data); setCategories(c.data); setSellers(sl.data); setGeneralSlots(me.data.tenant?.booking_slots || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm({ ...empty, category_id: categories[0]?.id || "" }); };
  const openEdit = (s) => { setEditing(s.id); setForm({ ...s, images: s.images || [], seller_ids: s.seller_ids || [], schedule: { ...emptySchedule, ...(s.schedule || {}) } }); };

  const save = async (e) => {
    e.preventDefault();
    const { schedule } = form;
    if (schedule.available_from && schedule.available_until && schedule.available_until < schedule.available_from) {
      toast.error("The end date must be on or after the start date"); return;
    }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/tenant/services", form);
      else await api.put(`/tenant/services/${editing}`, form);
      toast.success("Saved"); setEditing(null); load();
    } catch (err) {
      // The server says what is wrong with the slots, days or dates.
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "The service could not be saved");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/tenant/services/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="tadmin-services">
      <div className="flex flex-wrap justify-between items-end gap-3 mb-6">
        <div>
          <div className="admin-overline">Catalog</div>
          <h1 className="admin-heading text-3xl font-bold mt-1">Services</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setBulk((open) => !open)} aria-expanded={bulk} className="border border-black px-4 py-2.5 text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-2 hover:bg-neutral-50 min-h-[44px]" data-testid="tadmin-service-bulk-btn">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Bulk upload
          </button>
          <button onClick={openNew} className="bg-black text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-2 hover:bg-neutral-800 min-h-[44px]" data-testid="tadmin-service-new-btn">
            <Plus className="h-3.5 w-3.5" /> New service
          </button>
        </div>
      </div>

      {bulk && <BulkServiceUpload onClose={() => setBulk(false)} onImported={load} />}

      {editing && (
        <form onSubmit={save} className="bg-white border border-neutral-200 p-4 sm:p-6 mb-6 grid md:grid-cols-2 gap-4" data-testid="tadmin-service-form">
          <div><label className="admin-overline">Name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" data-testid="tadmin-service-form-name" /></div>
          <div><label className="admin-overline">Category</label>
            <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} required className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-black">
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="md:col-span-2"><label className="admin-overline">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" /></div>
          <div><label className="admin-overline">Price</label><input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) })} className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" data-testid="tadmin-service-form-price" /></div>
          <div><label className="admin-overline">Discount price</label><input type="number" value={form.discount_price || ""} onChange={(e) => setForm({ ...form, discount_price: e.target.value ? parseFloat(e.target.value) : null })} className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" /></div>
          <div><label className="admin-overline">Duration (min)</label><input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) })} className="w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black" /></div>
          <div className="md:col-span-2">
            <ImageUploader label="Service image" value={form.images[0] || ""} onChange={(url) => setForm({ ...form, images: url ? [url] : [] })} folder="services" shape="square" testid="tadmin-service-form-image" />
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-x-5 gap-y-2 items-center">
            <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={form.home_service} onChange={(e) => setForm({ ...form, home_service: e.target.checked })} /> Home service</label>
            <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={form.center_visit} onChange={(e) => setForm({ ...form, center_visit: e.target.checked })} /> Center visit</label>
            <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={form.requires_enquiry} onChange={(e) => setForm({ ...form, requires_enquiry: e.target.checked })} /> Enquiry only</label>
          </div>
          <div className="md:col-span-2">
            <label className="admin-overline">Assigned sellers</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {sellers.map((sl) => {
                const on = form.seller_ids.includes(sl.id);
                return (
                  <button key={sl.id} type="button"
                    onClick={() => setForm({ ...form, seller_ids: on ? form.seller_ids.filter((x) => x !== sl.id) : [...form.seller_ids, sl.id] })}
                    className={`px-3 py-1.5 text-xs ${on ? "bg-black text-white" : "bg-neutral-100"}`}>
                    {sl.business_name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="md:col-span-2 min-w-0">
            <ServiceScheduleEditor key={editing} value={form.schedule} onChange={(schedule) => setForm((f) => ({ ...f, schedule }))}
              duration={form.duration_minutes} generalSlots={generalSlots} />
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-2">
            <button type="submit" disabled={saving} className="bg-black text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider min-h-[44px] disabled:opacity-50" data-testid="tadmin-service-form-save">{saving ? "Saving..." : "Save"}</button>
            <button type="button" onClick={() => setEditing(null)} className="border border-neutral-300 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider min-h-[44px]">Cancel</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-neutral-200 table-scroll" data-testid="tadmin-services-table">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-200 bg-neutral-50">
            <tr className="text-left">
              <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Service</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Category</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Price</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Duration</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => {
              const cat = categories.find((c) => c.id === s.category_id);
              return (
                <tr key={s.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-4 py-3">
                    <div className="font-semibold">{s.name}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5" data-testid={`tadmin-service-schedule-${s.id}`}>{scheduleSummary(s.schedule)}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{cat?.name || "—"}</td>
                  <td className="px-4 py-3 font-mono">{formatINR(s.discount_price || s.price)}</td>
                  <td className="px-4 py-3 text-xs">{s.duration_minutes} min</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(s)} className="icon-btn hover:bg-neutral-100" aria-label="Edit" data-testid={`tadmin-service-edit-${s.id}`}><Edit2 className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(s.id)} className="icon-btn hover:bg-red-50 text-red-700" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
